/**
 * Theme cards for the Smart Product Filter & Search recommendation widgets.
 *
 * The app's recommendation blocks (Editor's choices, Recently viewed, Trending products,
 * Best sellers) render their own product cards, without the badges, wishlist and compare
 * buttons, stock status and ratings that every other card in the theme has. Each app card
 * is swapped for the theme's own card from snippets/card-product.liquid, fetched through
 * sections/smart-filter-cards.liquid, so the two cannot drift apart.
 *
 * Only the card inside each slide is replaced; the app's carousel is left alone. Cards are
 * fetched once per widget, up to 20 per request, and cached by handle so a product that
 * shows up in several widgets is fetched once. A card that cannot be fetched keeps the
 * app's markup.
 */
(() => {
  const WIDGET_SELECTOR = '.globo-related-products';
  const APP_CARD_SELECTOR = '.spf-product-card';
  const SECTION_ID = 'smart-filter-cards';
  // all_products resolves at most 20 distinct handles per render.
  const BATCH_SIZE = 20;

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cards = new Map(); // handle -> Promise<Element | null>
  const seen = new WeakSet();
  const observed = new WeakSet();

  function getHandle(appCard) {
    const link = appCard.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  async function fetchCards(handles) {
    const url =
      `${rootUrl}search?type=product&q=${encodeURIComponent(handles.join(','))}` +
      `&section_id=${SECTION_ID}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Smart filter cards: ${response.status} from ${url}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const found = new Map();
    doc.querySelectorAll('[data-smart-filter-card]').forEach((item) => {
      const card = item.querySelector('.card-wrapper');
      if (card) found.set(item.dataset.smartFilterCard, card);
    });
    return found;
  }

  function requestCards(handles) {
    const missing = [...new Set(handles)].filter((handle) => !cards.has(handle));
    for (let i = 0; i < missing.length; i += BATCH_SIZE) {
      const batch = missing.slice(i, i + BATCH_SIZE);
      const request = fetchCards(batch).catch((error) => {
        console.error(error);
        return new Map();
      });
      batch.forEach((handle) => cards.set(handle, request.then((found) => found.get(handle) || null)));
    }
  }

  async function upgrade(widget, appCards) {
    const handles = appCards.map(getHandle);
    requestCards(handles.filter(Boolean));

    await Promise.all(
      appCards.map(async (appCard, index) => {
        const card = handles[index] ? await cards.get(handles[index]) : null;
        if (!appCard.isConnected) return;
        if (card) {
          appCard.replaceWith(document.importNode(card, true));
        } else {
          // Shows the app's card again; see snippets/smart-filter-cards-script.liquid.
          appCard.dataset.themeCard = 'failed';
        }
      })
    );

    refresh(widget);
  }

  /**
   * The stock line and the review stars are filled in client-side, by code that only
   * covers the cards it finds when it runs, so both are re-run over the new cards.
   */
  function refresh(widget) {
    if (typeof window.updateStockStatus === 'function') {
      window.updateStockStatus(widget);
    }

    // Set by the reviews.io app embed on its first run, with the merchant's settings.
    const ratings = window.ratingSnippetGlobalSettings;
    if (typeof window.ratingSnippet === 'function' && ratings) {
      window.ratingSnippet(ratings.class, ratings.options);
    }
  }

  function scan(widget) {
    const appCards = [...widget.querySelectorAll(APP_CARD_SELECTOR)].filter((card) => !seen.has(card));
    if (!appCards.length) return;
    appCards.forEach((card) => seen.add(card));
    upgrade(widget, appCards);
  }

  // Recently viewed fills itself in after load, and the app may re-render a widget, so
  // each one is watched for app cards arriving later.
  function init() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach((widget) => {
      scan(widget);
      if (observed.has(widget)) return;
      observed.add(widget);
      new MutationObserver(() => scan(widget)).observe(widget, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
